import { ReportDataSchema } from './schema';

export function validateReportData(data: unknown): asserts data is ReportDataSchema {
  if (!data || typeof data !== 'object') {
    throw new Error('Invalid report data structure');
  }

  const report = data as any;

  // Validate required sections
  if (!Array.isArray(report.questions) || report.questions.length === 0) {
    throw new Error('Report must contain questions');
  }

  if (!report.feedbackComments || typeof report.feedbackComments !== 'object') {
    throw new Error('Report must contain feedback comments');
  }

  if (!report.metrics || typeof report.metrics !== 'object') {
    throw new Error('Report must contain metrics');
  }

  if (!report.analysis || typeof report.analysis !== 'object') {
    throw new Error('Report must contain analysis');
  }

  // Validate questions
  report.questions.forEach((question: any, index: number) => {
    if (!question.id || !question.text || !Array.isArray(question.evaluators)) {
      throw new Error(`Invalid question data at index ${index}`);
    }

    if (typeof question.selfAssessment !== 'number' || 
        question.selfAssessment < 0 || 
        question.selfAssessment > 10) {
      throw new Error(`Invalid self assessment score for question ${question.id}`);
    }

    question.evaluators.forEach((score: any) => {
      if (typeof score !== 'number' || score < 0 || score > 10) {
        throw new Error(`Invalid evaluator score for question ${question.id}`);
      }
    });

    if (!question.visualization || !['donut', 'spider', 'bar', 'line', 'radar'].includes(question.visualization)) {
      throw new Error(`Invalid visualization type for question ${question.id}`);
    }
  });

  // Validate feedback comments match questions
  report.questions.forEach(question => {
    if (!report.feedbackComments[question.id] || 
        !Array.isArray(report.feedbackComments[question.id]) ||
        report.feedbackComments[question.id].length === 0) {
      throw new Error(`Missing feedback comments for question ${question.id}`);
    }
  });

  // Validate metrics
  const metrics = report.metrics;
  if (typeof metrics.overallScore !== 'number' ||
      typeof metrics.strengthsCount !== 'number' ||
      typeof metrics.improvementAreas !== 'number' ||
      typeof metrics.completionRate !== 'number') {
    throw new Error('Invalid metrics data');
  }

  // Validate analysis
  const analysis = report.analysis;
  if (!Array.isArray(analysis.trends) ||
      !Array.isArray(analysis.recommendations) ||
      !Array.isArray(analysis.keyInsights)) {
    throw new Error('Invalid analysis data');
  }

  // Optional sections validation
  if (report.disc) {
    validateDiscData(report.disc);
  }

  if (report.leadership) {
    validateLeadershipData(report.leadership);
  }

  if (report.career) {
    validateCareerData(report.career);
  }

  if (report.collaboration) {
    validateCollaborationData(report.collaboration);
  }
}

function validateDiscData(disc: any) {
  if (!disc.profileSummary || !disc.behavioralTendencies || !disc.developmentOpportunities) {
    throw new Error('Invalid DISC data structure');
  }

  // Validate profile summary
  const summary = disc.profileSummary;
  if (!summary.style || !summary.intensity || !Array.isArray(summary.primaryTendencies)) {
    throw new Error('Invalid DISC profile summary');
  }

  // Validate behavioral tendencies
  const tendencies = disc.behavioralTendencies;
  if (!Array.isArray(tendencies.comfortZones) || !Array.isArray(tendencies.growthAreas)) {
    throw new Error('Invalid behavioral tendencies');
  }

  // Validate development opportunities
  const opportunities = disc.developmentOpportunities;
  if (!Array.isArray(opportunities.primaryFocus) || !Array.isArray(opportunities.recommendations)) {
    throw new Error('Invalid development opportunities');
  }
}

function validateLeadershipData(leadership: any) {
  if (!leadership.metrics || !leadership.insights) {
    throw new Error('Invalid leadership data structure');
  }

  // Validate metrics and insights have matching keys
  const metricKeys = Object.keys(leadership.metrics);
  const insightKeys = Object.keys(leadership.insights);

  if (!metricKeys.every(key => insightKeys.includes(key))) {
    throw new Error('Leadership metrics and insights must match');
  }

  // Validate metric values
  Object.values(leadership.metrics).forEach((value: any) => {
    if (typeof value !== 'number' || value < 0 || value > 5) {
      throw new Error('Leadership metrics must be between 0 and 5');
    }
  });
}

function validateCareerData(career: any) {
  if (!career.currentRole || !career.desiredRole || !career.skillGaps) {
    throw new Error('Invalid career data structure');
  }

  // Validate role data
  if (!career.currentRole.title || !career.currentRole.duration) {
    throw new Error('Invalid current role data');
  }

  if (!career.desiredRole.title || !career.desiredRole.timeToTransition) {
    throw new Error('Invalid desired role data');
  }

  // Validate skill gaps
  Object.entries(career.skillGaps).forEach(([skill, gap]: [string, any]) => {
    if (typeof gap.current !== 'number' || typeof gap.required !== 'number' ||
        gap.current < 0 || gap.current > 5 || gap.required < 0 || gap.required > 5) {
      throw new Error(`Invalid skill gap data for ${skill}`);
    }
  });
}

function validateCollaborationData(collaboration: any) {
  if (!collaboration.metrics || !collaboration.insights) {
    throw new Error('Invalid collaboration data structure');
  }

  // Validate metrics are numbers between 0 and 1
  Object.entries(collaboration.metrics).forEach(([metric, value]: [string, any]) => {
    if (typeof value !== 'number' || value < 0 || value > 1) {
      throw new Error(`Invalid collaboration metric value for ${metric}`);
    }
  });

  // Validate insights exist for all metrics
  const metricKeys = Object.keys(collaboration.metrics);
  const insightKeys = Object.keys(collaboration.insights);

  const normalizedMetricKeys = metricKeys.map(key => key.toLowerCase().replace(/\s+/g, ''));
  const normalizedInsightKeys = insightKeys.map(key => key.toLowerCase().replace(/\s+/g, ''));

  if (!normalizedMetricKeys.every(key => normalizedInsightKeys.includes(key))) {
    throw new Error('Collaboration metrics and insights must match');
  }
}